import {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Location } from "@services/locations/types";
import { useErrorNotification } from "@common/components/Notification";
import { getLocation } from "@services/locations";
import { setDefaultLocationMapImage } from "@services/logo";

export const LocationDetailContext = createContext<undefined | Location>(
  undefined
);

export const LocationDetailDispatchContext = createContext<{
  refetch: (fullRefresh?: boolean) => Promise<void>;
}>({ refetch: async () => {} });

export const LocationDetailProvider = ({ children }: PropsWithChildren<{}>) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locationid } = useParams();

  const [location, setLocation] = useState<Location>();

  const dispatchError = useErrorNotification();

  const fetch = async (locationId: string) => {
    let location: Location | undefined;
    try {
      location = await getLocation(locationId);
      const { longitude, latitude, id } = location;
      if (
        !Boolean(location.logo) &&
        latitude !== undefined &&
        longitude !== undefined &&
        id !== undefined
      )
        location.logo = await setDefaultLocationMapImage({
          latitude,
          longitude,
          id,
        });
    } catch (e) {
      navigate("/");
      dispatchError(e, { resourceName: t("Location") });
    } finally {
      // Setter in a finally block, to still be executed if an error only happened on the logo generation/upload
      if (location) setLocation(location);
    }
  };

  const refetch = useCallback(
    async (fullRefresh: boolean = false) => {
      if (fullRefresh) setLocation(undefined);
      if (locationid !== undefined) await fetch(locationid);
    },
    [locationid]
  );

  useEffect(() => {
    refetch();
  }, [locationid]);

  return (
    <LocationDetailContext.Provider value={location}>
      <LocationDetailDispatchContext.Provider value={{ refetch: refetch }}>
        {children}
      </LocationDetailDispatchContext.Provider>
    </LocationDetailContext.Provider>
  );
};
